import React from 'react';
import styled from 'styled-components';
import { useOrg } from '../../org';
import useSessionTimeline from '../hooks/useSessionTimeline';
import Agenda from './Agenda';
import {
  Chip,
  PrintColors,
  PrintHeaderSection,
  PrintIcon,
  PrintSubTitle,
  PrintTransition,
  SegmentHeaderContainer,
  FONT_SIZE_SCALE,
} from './PrintElements';
import PrintHeader from './PrintHeader';
import PrintSection from './PrintSection';
import { Section, Segment } from '../models/timeline';
import sessionTimelineService from '../../../services/sessionTimelineService';
import Note from './Note';

export type PrintPreferences = {
  viewPreference: string;
  fontSize: keyof typeof FONT_SIZE_SCALE;
  printStyle: 'color' | 'blackAndWhite';
  includeAgenda: boolean;
  media: boolean;
  suppliesOnly: boolean;
};

const shouldShowSection = (section: Section, printPreferences: PrintPreferences) => {
  if (printPreferences.suppliesOnly) {
    return section._type === 'supplies';
  }
  return true;
};

const shouldShowSegment = (segment: Segment, viewPreference: string) => {
  return !viewPreference || sessionTimelineService.matchesLeaderViewPreference(viewPreference, segment.leader);
};

const getSegmentSections = (segment: Segment): Section[] => {
  if (segment.isFlex && segment.options) {
    return segment.options.filter(opt => opt.enabled).flatMap(opt => opt.sections);
  }
  return segment.sections;
};

const PrintContainer = styled.div<{ fontSize: keyof typeof FONT_SIZE_SCALE; printStyle: 'color' | 'blackAndWhite' }>`
  ${`@import url('https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:ital,wght@0,400..900;1,400..900&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap');`}
  display: none;

  @media print {
    display: block;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    ${({ printStyle }) =>
      printStyle === 'blackAndWhite' &&
      `
        -webkit-filter: grayscale(100%) url('#print-grayscale') !important;
        filter: grayscale(100%) url('#print-grayscale') !important;
      `}
  }
  font-family: 'Schibsted Grotesk', sans-serif;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0;
    font-family: 'Ubuntu', sans-serif !important;
  }

  p {
    margin: 0;
    font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].base};
    font-family: 'Ubuntu', sans-serif !important;
  }
`;

const SegmentGroupIconMap = Object.freeze({
  leaders: 'fas fa-chalkboard-teacher',
  small: 'fas fa-user-friends',
  large: 'fas fa-users',
});

type SectionTypeMap = ReturnType<typeof useSessionTimeline>['sectionTypeMap'];

const FlexSegmentSections = ({
  options,
  printPreferences,
  sectionTypeMap,
}: {
  options: Segment[];
  printPreferences: PrintPreferences;
  sectionTypeMap: SectionTypeMap;
}) => {
  const enabledOptions = options.filter(option => option.enabled);
  const showOptionNumber = enabledOptions.length > 1;

  return (
    <>
      {enabledOptions.map((option, optionIndex) => (
        <React.Fragment key={option.segmentId}>
          {option.sections.filter(section => shouldShowSection(section, printPreferences)).length > 0 && (
            <PrintSubTitle fontSize={printPreferences.fontSize}>
              {showOptionNumber && `Option ${optionIndex + 1}: `}
              {option.name}
              {option.activity && <span> / {option.activity}</span>}
            </PrintSubTitle>
          )}
          {option.sections
            .filter(section => shouldShowSection(section, printPreferences))
            .map(section => (
              <PrintSection
                key={section.id}
                section={section}
                segment={option}
                printPreferences={printPreferences}
                sectionTypeMap={sectionTypeMap}
              />
            ))}
        </React.Fragment>
      ))}
    </>
  );
};

export default function PrintableTimeline({ printPreferences }: { printPreferences: PrintPreferences }) {
  const { org } = useOrg();
  const { timeline, sectionTypeMap, segmentGroupMap, segmentLeaderMap } = useSessionTimeline();

  const showGroupHeader = (segment: Segment, i: number, printPreferences: PrintPreferences) => {
    if (
      i === 0 &&
      getSegmentSections(segment).filter(section => shouldShowSection(section, printPreferences)).length > 0
    )
      return true; // First segment always shows header unless all segments are hidden
    if (i === 0) return false; // First segment with no visible sections shouldn't show header
    return segment.group !== timeline.segments[i - 1].group;
  };

  return (
    <PrintContainer fontSize={printPreferences.fontSize} printStyle={printPreferences.printStyle}>
      {printPreferences.printStyle === 'blackAndWhite' && (
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
          <filter id="print-grayscale">
            <feColorMatrix
              type="matrix"
              values="0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0"
            />
          </filter>
        </svg>
      )}
      <PrintHeader
        orgName={org?.name ?? ''}
        fontSize={printPreferences.fontSize}
        includeHeaderImage={printPreferences.media && !printPreferences.suppliesOnly}
      />

      {printPreferences.includeAgenda && !printPreferences.suppliesOnly && (
        <Agenda segments={timeline.segments} fontSize={printPreferences.fontSize} />
      )}

      {!!timeline.segments.length &&
        timeline.segments.map((segment, i) => (
          <React.Fragment key={segment.segmentId}>
            {showGroupHeader(segment, i, printPreferences) && (
              <PrintTransition>
                <SegmentHeaderContainer>
                  <PrintIcon
                    fontSize={printPreferences.fontSize}
                    color={PrintColors.LightGray}
                    className={SegmentGroupIconMap[segment.group as keyof typeof SegmentGroupIconMap]}
                  />
                  <PrintSubTitle fontSize={printPreferences.fontSize}>
                    {segmentGroupMap[segment.group as keyof typeof segmentGroupMap]}
                  </PrintSubTitle>
                </SegmentHeaderContainer>
              </PrintTransition>
            )}
            {shouldShowSegment(segment, printPreferences.viewPreference) && (
              <>
                {getSegmentSections(segment).filter(section => shouldShowSection(section, printPreferences)).length >
                  0 && (
                  <PrintHeaderSection>
                    <SegmentHeaderContainer>
                      <PrintSubTitle fontSize={printPreferences.fontSize}>
                        {printPreferences.suppliesOnly ? '' : i + 1 + '. '} {segment.name}{' '}
                        <span> / {segmentLeaderMap[segment.leader as keyof typeof segmentLeaderMap]}</span>
                      </PrintSubTitle>
                    </SegmentHeaderContainer>
                    {segment.duration > 0 ? (
                      <Chip color={PrintColors.LightGreen} fontSize={printPreferences.fontSize}>
                        <p>{segment.duration} minutes</p>
                      </Chip>
                    ) : (
                      <div />
                    )}
                  </PrintHeaderSection>
                )}
                {segment.note && !printPreferences.suppliesOnly && (
                  <Note title={segment.note.title} text={segment.note.body} fontSize={printPreferences.fontSize} />
                )}
                {segment.isFlex && segment.options ? (
                  <FlexSegmentSections
                    options={segment.options}
                    printPreferences={printPreferences}
                    sectionTypeMap={sectionTypeMap}
                  />
                ) : (
                  segment.sections
                    .filter(section => shouldShowSection(section, printPreferences))
                    .map(section => (
                      <PrintSection
                        key={section.id}
                        section={section}
                        segment={segment}
                        printPreferences={printPreferences}
                        sectionTypeMap={sectionTypeMap}
                      />
                    ))
                )}
              </>
            )}
          </React.Fragment>
        ))}
    </PrintContainer>
  );
}
